import { createSelector } from '@reduxjs/toolkit';
import { clamp, first } from 'lodash';
import { DefaultRootState } from 'react-redux';
import { roundedDurationSelector } from '../../ecg/selectors/ecg.selectors';
import { visibleStripsSelector } from '../../plot/selectors/strip.selectors';

export const navigationSelector = ({ navigation }: DefaultRootState) =>
  navigation;

export const baseTimestampSelector = createSelector(
  navigationSelector,
  ({ timestamp }) => timestamp,
);

export const smallestStripSelector = createSelector(
  visibleStripsSelector,
  (strips) => {
    const sortedStrips = strips.sort((a, b) => a.duration - b.duration);
    return first(sortedStrips);
  },
);

export const minTimestampSelector = createSelector(
  smallestStripSelector,
  (strip) => {
    if (strip == null) {
      return 0;
    }

    return strip.duration / 2;
  },
);

export const maxTimestampSelector = createSelector(
  roundedDurationSelector,
  minTimestampSelector,
  smallestStripSelector,
  (ecgDuration, minTimestamp, strip) => {
    if (strip == null) {
      return ecgDuration - minTimestamp;
    }
    const { offset, onset } = strip;
    return ecgDuration + offset + onset - minTimestamp;
  },
);

export const timestampSelector = createSelector(
  baseTimestampSelector,
  minTimestampSelector,
  maxTimestampSelector,
  (timestamp, minTimestamp, maxTimestamp) => {
    if (minTimestamp > maxTimestamp) {
      return clamp(timestamp, maxTimestamp, minTimestamp);
    }

    return clamp(timestamp, minTimestamp, maxTimestamp);
  },
);

export const offsetSelector = createSelector(
  navigationSelector,
  ({ offset }) => offset,
);

export const activeSelector = createSelector(
  navigationSelector,
  ({ active }) => active,
);
